import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CookieTable } from '../components/CookieTable';
import { PublicShell } from '../components/PublicShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';
import { useConsent } from '../consent/ConsentContext';

const SECTIONS = [
  'controller',
  'dataWeCollect',
  'legalBasis',
  'recipients',
  'transfers',
  'retention',
  'cookies',
  'gdprRights',
  'ccpaRights',
  'children',
  'security',
  'extension',
  'changes',
  'contact',
] as const;

type SectionKey = (typeof SECTIONS)[number];

const ROUTE = '/privacy';
const OG_IMAGE = 'https://markflow.fyi/og.png';

export function PrivacyPolicyPage() {
  const { t, i18n } = useTranslation();
  const { openModal } = useConsent();
  const lang = activeLang(i18n.resolvedLanguage);
  const pageUrl = publicUrl(ROUTE, lang);

  usePageMeta({
    title: t('privacy.meta.title'),
    description: t('privacy.meta.description'),
    canonical: pageUrl,
    ogTitle: t('privacy.meta.ogTitle'),
    ogDescription: t('privacy.meta.ogDescription'),
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(ROUTE),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('privacy.meta.title'),
      description: t('privacy.meta.description'),
      url: pageUrl,
    },
  });

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    // Defer to next frame so the DOM (including anchor targets) is present.
    requestAnimationFrame(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const renderSection = (key: SectionKey) => {
    if (key === 'cookies') {
      return (
        <section key={key} id="cookies" className="space-y-3 scroll-mt-20">
          <h2 className="text-xl font-semibold">{t('privacy.sections.cookiesTitle')}</h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
            {t('privacy.sections.cookiesBody')}
          </p>
          <CookieTable />
        </section>
      );
    }

    const bodyKey = `privacy.sections.${key}.body`;
    const titleKey = `privacy.sections.${key}.title`;

    return (
      <section key={key} className="space-y-3">
        <h2 className="text-xl font-semibold">{t(titleKey)}</h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed whitespace-pre-line">
          {t(bodyKey)}
        </p>
        {key === 'gdprRights' && (
          <Button variant="outline" onClick={openModal}>
            {t('privacy.manageCookiesCta')}
          </Button>
        )}
      </section>
    );
  };

  return (
    <PublicShell>
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
          <Link href="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('footer.backToApp')}
          </Link>

          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('privacy.title')}
            </h1>
            <p className="text-xs text-zinc-500">{t('privacy.lastUpdated')}</p>
          </div>
          <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">{t('privacy.intro')}</p>

          {SECTIONS.map(renderSection)}
        </div>
      </main>
    </PublicShell>
  );
}
